import { Box, Dialog, DialogProps } from "@mui/material";
import {
  ReactElement,
  ReactNode,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import { ReactHelper } from "@/common/helpers/react";

import AppMountedSensor from "../AppMountedSensor";
import { AppModalActionsProps } from "./AppModalActions";
import AppModalTitle, { AppModalTitleProps } from "./AppModalTitle";

/** Time to wait after Popover open is requested and then it can be considered as opened. */
// const openWaitMs = 300;
/** Time to wait before closing Popover after delayed close was requested. */
// const closeDelayedWaitMs = 100;

export type AppModalOnCloseFunc = (
  event?: any,
  reason?:
    | "backdropClick"
    | "escapeKeyDown"
    | "closeButtonClick"
    | "imperativeClose"
    | "childClose",
) => void;

export interface AppPopoverHoverBehaviorProps {
  /**
   * onTriggerMouseLeave - popover is closed when mouse leaves the trigger.
   * onContentMouseLeave - popover is closed when mouse enters and then leaves the content.
   * onTriggerOrContentMouseLeave - combination of onTriggerMouseLeave and onContentMouseLeave.
   *  If mouse leaves the trigger and then enters the content during short time, then popover is not closed.
   */
  closeBehavior?: "onTriggerMouseLeave" | "onContentMouseLeave" | "onTriggerOrContentMouseLeave";
}

export type AppModalHandle = {
  open: () => void;
  close: () => void;
};

export interface AppModalChildrenParams {
  isOpen: boolean;
  titleProps: Partial<AppModalTitleProps>;
  actionProps: Partial<AppModalActionsProps>;
  close: AppModalOnCloseFunc;
}

export type AppModalProps = {
  /** Whether modal is enabled.
   * @default true
   */
  enabled?: boolean;
  /** Element that triggers modal open. */
  trigger?: ((params: { isOpen: boolean }) => ReactElement) | ReactElement;
  triggerParams?: {
    /** If `true`, default trigger action (open modal on click) is not applied, so it can be handled manually in trigger element onClick. */
    isDisableDefaultAction?: boolean;
  };
  titleProps?: AppModalTitleProps;
  /** Modal content. */
  children?: ((params: AppModalChildrenParams) => ReactNode) | ReactNode;
  debugKey?: string;
  onClose?: AppModalOnCloseFunc;
  /** Fired when model is opened (its content is mounted). */
  onOpen?: () => void;
} & Partial<Omit<DialogProps, "ref" | "onClose" | "children">>;

// export type AppModalInheritableProps = Omit<AppModalProps, "children"> & {
//   children?: AppModalProps["children"];
// };

export default forwardRef<AppModalHandle, AppModalProps>(function AppModal(
  {
    enabled = true,
    trigger,
    triggerParams,
    titleProps,
    children,
    debugKey,
    onClose,
    onOpen,
    ...otherProps
  }: AppModalProps,
  ref,
) {
  const triggerRef = useRef<HTMLElement | undefined>(undefined);
  const [isOpen, setIsOpen] = useState<boolean>(otherProps.open ?? false);

  const isOpenComputed = useMemo(() => otherProps.open ?? isOpen, [otherProps.open, isOpen]);

  const open = () => {
    setIsOpen(true);
  };

  const close: AppModalOnCloseFunc = (e, reason) => {
    setIsOpen(false);
    onClose?.(e, reason || "escapeKeyDown");
  };

  const closeByChildren: AppModalOnCloseFunc = (e, reason) => {
    close(e || undefined, reason || "childClose");
  };

  const triggerElement = useMemo(() => {
    const element = typeof trigger === "function" ? trigger({ isOpen: isOpenComputed }) : trigger;
    if (!element) {
      return null;
    }
    if (triggerParams?.isDisableDefaultAction) {
      return element;
    }
    return ReactHelper.cloneWithRef(element, {
      ref: triggerRef,
      onClick: (e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault();
        e.stopPropagation();
        open();
      },
    });
  }, [trigger, triggerParams?.isDisableDefaultAction]);

  useImperativeHandle(
    ref,
    () => ({
      open() {
        open();
      },
      close() {
        close(undefined, "imperativeClose");
      },
    }),
    [triggerRef.current, open, close],
  );

  return (
    // use React.Fragment to render trigger directly without wrapping
    <>
      {triggerElement}

      {enabled && (
        <Dialog
          {...otherProps}
          open={isOpenComputed}
          onClose={(e, reason) => {
            close(e, reason);
          }}
        >
          <Box>
            {titleProps && (
              <AppModalTitle
                {...titleProps}
                onClose={() => {
                  close(undefined, "closeButtonClick");
                  titleProps.onClose?.();
                }}
              />
            )}

            <Box>
              {typeof children === "function"
                ? children({
                    isOpen: isOpenComputed,
                    titleProps: {
                      onClose: closeByChildren,
                    },
                    actionProps: {
                      onClose: closeByChildren,
                    },
                    close: closeByChildren,
                  })
                : children}
            </Box>

            {/* Track when model is opened. */}
            <AppMountedSensor
              onMounted={() => {
                onOpen?.();
              }}
            />
          </Box>
        </Dialog>
      )}
    </>
  );
});
